import { MutationKey } from "@tanstack/react-query";
import { MutationConfig } from "./types";
import { sanitizeKey } from "./utils";

/**
 * Creates a reusable mutation definition for TanStack Query.
 *
 * This function allows you to define a mutation with a key and mutation function,
 * which can be called later with optional variables. It supports both static
 * and dynamic mutation keys based on the provided variables. Mutations are used
 * for operations that modify data (POST, PUT, DELETE, etc.).
 *
 * @template TVariables - The type of variables passed to the mutation (defaults to void)
 * @template TData - The type of data returned by the mutation
 *
 * @param config - Configuration object for the mutation
 * @param config.mutationKey - Static mutation key or a function that generates a key from variables
 * @param config.mutationFn - Async function that performs the mutation using the provided variables
 *
 * @returns A function that accepts optional variables and returns a MutationConfig object
 *
 * @example
 * const createUserMutation = defineMutation({
 *   mutationKey: ['createUser'],
 *   mutationFn: (vars) => createUser(vars.name, vars.email)
 * });
 */
export function defineMutation<TVariables = void, TData = unknown>(config: {
  mutationKey: MutationKey | ((variables: TVariables) => MutationKey);
  mutationFn: (variables: TVariables) => Promise<TData>;
}) {
  return (options?: TVariables) => {
    const mutationKey =
      typeof config.mutationKey === "function"
        ? config.mutationKey(options as TVariables)
        : config.mutationKey;

    return {
      type: "mutation" as const,
      mutationKey: sanitizeKey(mutationKey),
      mutationFn: config.mutationFn,
    } satisfies MutationConfig<TVariables, TData>;
  };
}
